import { h, Fragment, FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptionsWithLinkToken,
  PlaidLinkStableEvent,
  usePlaidLink,
} from 'react-plaid-link';
import { batch, useDispatch, useSelector } from 'react-redux';
import { PaymentOption } from '~src/domains/common/enums/common';
import { PlaidErrorCode, PlaidEvent } from '~src/domains/common/enums/plaid';
import {
  setBankMetadata,
  setPaymentMethod,
  setPlaidLinkToken,
} from '~src/domains/common/state/commonReducer';
import {
  bankMetadataSelector,
  localeSelector,
  plaidLinkTokenSelector,
} from '~src/domains/common/state/commonSelectors';
import { selectBankAccount } from '~src/utils/helpers';
import { trackClick, trackHandler } from '~src/utils/tracking';
import { xhrPlaidLinkToken } from '~src/utils/xhr';

type PlaidLinkProps = {
  label?: string;
};

const PlaidLink: FunctionComponent<PlaidLinkProps> = ({ label }) => {
  const dispatch = useDispatch();
  const [linkClicked, setLinkClicked] = useState<boolean>(false);

  const bankMetadata = useSelector(bankMetadataSelector);
  const locale = useSelector(localeSelector);
  const linkToken = useSelector(plaidLinkTokenSelector);

  // If a label is given, PlaidLink serves as a button that will open the Plaid modal.
  // Otherwise, it will open the Plaid modal on mount.
  const showButton = !!label;

  const onSuccess = (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    const accounts = metadata.accounts;
    const accountsCount = accounts.length;
    const selectedAccount = selectBankAccount(metadata.accounts);
    const institution = metadata.institution?.name;

    batch(() => {
      dispatch(
        trackHandler('plaid', {
          account: selectedAccount,
          target: 'bank-account-modal',
          'link-session-id': metadata.link_session_id,
          'institution-name': institution,
          'number-of-accounts': accountsCount,
          'auto-select-account': accountsCount > 1,
          'event-name': 'success',
        }),
      );

      dispatch(
        setBankMetadata({
          institution,
          account: selectedAccount,
          public_token: publicToken,
        }),
      );

      dispatch(setPlaidLinkToken(null)); // Plaid link token can only be used once in production.
    });
  };

  const onExit = (
    error: PlaidLinkError | null,
    metadata: PlaidLinkOnExitMetadata,
  ) => {
    if (error?.error_code === PlaidErrorCode.INVALID_LINK_TOKEN) {
      dispatch(setPlaidLinkToken(null));
      dispatch(xhrPlaidLinkToken(locale, true));
    } else if (!bankMetadata) {
      dispatch(setPaymentMethod('')); // Reset payment method otherwise it won't open
      setLinkClicked(false);
    } else {
      setLinkClicked(false);
    }
  };

  const onEvent = (
    eventName: PlaidLinkStableEvent | string,
    metadata: PlaidLinkOnEventMetadata,
  ) => {
    let optionalEventData = {};
    switch (eventName) {
      case PlaidEvent.TRANSITION_VIEW:
        optionalEventData = {
          'view-name': metadata.view_name,
        };
      case PlaidEvent.EXIT:
        optionalEventData = {
          status: metadata.exit_status,
          'error-type': metadata.error_type,
          'error-message': metadata.error_message,
          'error-code': metadata.error_code,
        };
      case PlaidEvent.SEARCH_INSTITUTION:
        optionalEventData = {
          'search-query': metadata.institution_search_query,
        };
      case PlaidEvent.ERROR:
        optionalEventData = {
          'error-type': metadata.error_type,
          'error-message': metadata.error_message,
          'error-code': metadata.error_code,
        };
    }

    const eventData = {
      ...optionalEventData,
      target: 'bank-account-modal',
      'link-session-id': metadata.link_session_id,
      'institution-name': metadata?.institution_name,
      'event-name': eventName.toLowerCase(),
    };

    dispatch(trackHandler('plaid', eventData));
  };

  const config: PlaidLinkOptionsWithLinkToken = {
    onSuccess,
    onExit,
    onEvent,
    token: linkToken,
  };

  const { open, ready } = usePlaidLink(config);

  // On mount, generate and set the Plaid handler once
  useEffect(() => {
    if (!open || !ready) {
      return;
    }

    if (!showButton || (linkClicked && linkToken)) {
      open();
    }
  }, [open, ready, linkToken, showButton, linkClicked]);

  const openPlaidModal = () => {
    setLinkClicked(true);

    batch(() => {
      dispatch(
        trackClick('open-bank-account-button', {
          target: 'button',
          label: label || '',
          'payment-method': PaymentOption.BANK_ACCOUNT,
        }),
      );

      if (!linkToken) {
        dispatch(xhrPlaidLinkToken(locale, true));
      }
    });
  };

  return label ? (
    <button onClick={openPlaidModal}>{label}</button>
  ) : (
    <Fragment />
  );
};

export default PlaidLink;
